import { useState, useEffect, useCallback } from "react";
import AgeSlider from "components/atoms/AgeSlider";
import Button from "components/atoms/Button";
import ReminderSettings from "components/molecules/ReminderSettings";
import {
  calculateBirthdayTimestamp,
  calculateAgeFromTimestamp,
  isReadyForLearning,
  formatAge,
} from "utils/ageCalculations";
import "./styles.scss";

const AgeSelector = ({ card, baseUrl, initialData = {} }) => {
  const [years, setYears] = useState(4);
  const [months, setMonths] = useState(5);
  const [showMonths, setShowMonths] = useState(true);
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    type: "email",
    phone: "",
  });
  const [savedIndicators, setSavedIndicators] = useState({
    age: false,
    reminder: false,
    phone: false,
  });

  // Ініціалізуємо значення з сервера, якщо є
  useEffect(() => {
    if (initialData.roundedBirthday) {
      const calculatedAge = calculateAgeFromTimestamp(
        initialData.roundedBirthday
      );
      setYears(calculatedAge.years);
      setMonths(calculatedAge.months);
    }

    if (initialData.phone) {
      setReminderSettings((prev) => {
        if (prev.phone !== initialData.phone) {
          return { ...prev, phone: initialData.phone };
        }
        return prev;
      });
    }

    // Використовуємо remindersLeft: якщо > 0, то нагадування включені
    if (initialData.remindersLeft !== undefined) {
      const newEnabled = initialData.remindersLeft > 0;
      setReminderSettings((prev) => {
        if (prev.enabled !== newEnabled) {
          return { ...prev, enabled: newEnabled };
        }
        return prev;
      });
    }

    // Set reminder type only if it's different from current
    const finalReminderType = initialData.reminderType || "email";
    setReminderSettings((prev) => {
      if (prev.type !== finalReminderType) {
        return {
          ...prev,
          type: finalReminderType,
        };
      }
      return prev;
    });
  }, [initialData]);

  // Показувати слайдер місяців тільки для дітей до 5 років включно
  useEffect(() => {
    setShowMonths(years < 5);
    if (years >= 5) {
      setMonths(0);
    }
  }, [years]);

  // Debounced збереження даних на сервер
  const debouncedSave = useCallback((param, value, indicatorKey) => {
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `/user-data/${param}=${encodeURIComponent(value)}`
        );
        if (response.ok) {
          // Показуємо індикатор збереження
          setSavedIndicators((prev) => ({ ...prev, [indicatorKey]: true }));
          // Ховаємо через 5 секунд
          setTimeout(() => {
            setSavedIndicators((prev) => ({ ...prev, [indicatorKey]: false }));
          }, 5000);
        } else {
          console.error("Failed to save data to server");
        }
      } catch (error) {
        console.error("Error saving to server:", error);
      }
    }, 500); // Debounce 0.5 секунди

    return () => clearTimeout(timeoutId);
  }, []);

  // Зберігаємо дані про вік при зміні
  useEffect(() => {
    const timestamp = calculateBirthdayTimestamp(years, months);
    const cleanup = debouncedSave("roundedBirthday", timestamp, "age");
    return cleanup;
  }, [years, months, debouncedSave]);

  const handleYearsChange = (newYears) => {
    setYears(newYears);
  };

  const handleMonthsChange = (newMonths) => {
    setMonths(newMonths);
  };

  const handleReminderChange = ({ enabled, type, phone }) => {
    setReminderSettings({ enabled, type, phone });

    // Використовуємо remindersLeft: 2 при включенні, 0 при вимкненні
    debouncedSave("remindersLeft", enabled ? 2 : 0, "reminder");
    debouncedSave("reminderSettings", type, "reminder");

    if (phone && type === "sms") {
      debouncedSave("phone", phone.replace(/[^\d+]/g, ""), "phone");
    }
  };

  const handlePhoneChange = (phone) => {
    if (reminderSettings.type === "sms") {
      debouncedSave("phone", phone.replace(/[^\d+]/g, ""), "phone");
    }
  };

  const ready = isReadyForLearning(years, months);
  const formattedAge = formatAge(years, months);

  return (
    <div className="age-selector">
      <div className="age-selector-header">
        <h2>Скільки років вашій дитині?</h2>
        <p className="age-display">
          Вказаний вік: <span className="age-value">{formattedAge}</span>
        </p>
      </div>

      <div className="age-controls">
        <div className="age-slider-section">
          <div className="slider-header">
            <label className="slider-label">Повних років:</label>
            {savedIndicators.age && (
              <div className="save-indicator">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="#22c55e"
                  />
                </svg>
              </div>
            )}
          </div>
          <AgeSlider
            type="years"
            value={years}
            onChange={handleYearsChange}
            max={8}
          />
        </div>

        {showMonths && (
          <div className="age-slider-section months-section">
            <div className="slider-header">
              <label className="slider-label">Повних місяців:</label>
              {savedIndicators.age && (
                <div className="save-indicator">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                      fill="#22c55e"
                    />
                  </svg>
                </div>
              )}
            </div>
            <AgeSlider
              type="months"
              value={months}
              onChange={handleMonthsChange}
              max={11}
            />
          </div>
        )}
      </div>

      {ready ? (
        <div>
          <div className="success-message">
            <h2>Повна готовність</h2>
            <p>Ваша дитина готова опановувати читання із «Ребус-методом».</p>
            <p>
              Навчатися можна після чотирьох років або від часу, коли мовлення
              малюка розуміють не тільки близькі йому дорослі.
            </p>
          </div>
        </div>
      ) : (
        <div className="not-ready-section">
          <h2>Створимо нагадування, щоб розпочати вчасно</h2>
          <p>
            Хіба що мовлення вашої дитини вже зрозуміле не тільки вам, а й іншим
            дорослим — радимо трохи зачекати. На цьому етапі важливіші фізичний
            та емоційний розвиток дитини. Навчання читанню принесе їй більше
            задоволення, коли вона підросте.
          </p>
          <p>
            Якщо дозволите, ми нагадаємо вам — поштою або SMS — коли настане
            час. Ми не спамимо і не передаємо ваші дані третім особам. Максимум
            два нагадування. Обіцяємо.
          </p>
          <ReminderSettings
            onReminderChange={handleReminderChange}
            onPhoneChange={handlePhoneChange}
            initialPhone={reminderSettings.phone}
            initialReminderEnabled={reminderSettings.enabled}
            initialReminderType={reminderSettings.type}
            // Pass current state for optimistic updates
            currentPhone={reminderSettings.phone}
            currentReminderEnabled={reminderSettings.enabled}
            currentReminderType={reminderSettings.type}
            savedIndicators={savedIndicators}
          />
          <p className="blockquote">
            Особливості розвитку дітей такі, що до чотирьох років вони не здатні
            мислити логічно — їм доступне лише образне мислення. Читання ж
            вимагає абстрактного мислення: читач має співвідносити образи
            й&nbsp;узагальнювати їх у&nbsp;поняття. У&nbsp;маленьких дітей також
            не розвинені ритмічний і&nbsp;понятійний рівні сприйняття, необхідні
            для читання.
          </p>
          <p>
            Якщо малеча ще не підросла, не переживайте, ви зможете повернутися
            до навчання пізніше. Поки що вчіть абетку 😊 І краще відразу вчити
            звуки, а&nbsp;не літери. Вчіть «Б», а&nbsp;не «Бе», «С», а&nbsp;не
            «Ес». А&nbsp;як підростете трохи, побачимось: понад 45 000 батьків
            довірили нам навчання своїх дітей.
          </p>
          <p>Вам все одно цікаво спробувати? Звичайно ж спробуйте:</p>
        </div>
      )}

      <Button
        className="available"
        onClick={() => card?.next()}
        text="Як гратися?"
      />
    </div>
  );
};

export default AgeSelector;
